import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Save, CheckCircle2, FileText, Plus, Trash2, Camera, X, ListChecks, ChevronDown, ArrowLeft, ClipboardCheck, CheckCircle, AlertTriangle, Clock, Search, Edit } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { getChecklistTemplate } from '@/lib/checklists/registry';
import { getSystemCategory } from '@/lib/systemTypeConstants';
import { filtrarPorTipoRevision, TIPO_REVISION_LABEL, type TipoRevision } from '@/lib/checklists/types';
import { sugerirProximaRevision } from '@/lib/coberturaRevisiones';
import { AnomaliaSelector } from '@/components/anomalias/AnomaliaSelector';
import { AnomaliasIndicator } from '@/components/anomalias/AnomaliasIndicator';
import { useAnomalias, type CodigoAnomalia } from '@/hooks/useAnomalias';
import { ELEMENTO_COLUMNS } from '@/lib/inventario/elementoColumns';
import { getFieldsForTipo } from '@/lib/inventario/fieldSchemas';
import { useElementoColumnPrefs } from '@/hooks/useElementoColumnPrefs';
import { ColumnasElementosPicker } from '@/components/inventario/ColumnasElementosPicker';
import { ElementoFotosButton } from '@/components/inventario/ElementoFotosButton';

import { ElementoEditDialog } from '@/components/inventario/ElementoEditDialog';
import { PruebaPrestacionesBlock, emptyPruebaPrestaciones, normalizePruebaPrestaciones, type PruebaPrestacionesValue, type BombaInventario, type TipoBomba, type InstrumentosSala } from '@/components/checklist/PruebaPrestacionesBlock';
import { parseUnidadCaudal, parseUnidadPresion } from '@/lib/checklists/units';
import { resolveInstrumentosGrupoPresion } from '@/lib/checklists/instrumentosGrupoPresion';
import { cn } from '@/lib/utils';


export interface ChecklistRevisionPageProps {
  sistemaId: string;
  sistemaTipo: string;
  clienteId: string;
  clienteNombre?: string;
  /** Si se pasa, abre el borrador existente. */
  checklistId?: string;
  onClose: () => void;
  onSaved?: () => void;
}

type Respuesta = 'si' | 'no' | 'na';
interface RespuestaItem {
  respuesta?: Respuesta;
  observaciones?: string;
}

type Prioridad = 'alta' | 'media' | 'baja';
type TipoObs = 'normativa' | 'mejora';
interface ObservacionItem {
  id: string;
  prioridad: Prioridad;
  tipo: TipoObs;
  texto: string;
  fotos?: string[];
  codigoAnomalia?: string;
  referenciaNormativa?: string;
  justificacionNormativa?: string;
}

const PRIORIDAD_STYLES: Record<Prioridad, string> = {
  alta: 'bg-red-500/20 text-red-600 border-red-500/30',
  media: 'bg-yellow-500/20 text-yellow-600 border-yellow-500/30',
  baja: 'bg-blue-500/20 text-blue-600 border-blue-500/30',
};
const formatObservacionesParaPdf = (obs: ObservacionItem[]): string => {
  if (!obs.length) return '';
  return obs
    .filter(o => o.texto.trim() || o.justificacionNormativa)
    .map((o, i) => {
      const prio = o.prioridad.toUpperCase();
      const tipo = o.tipo === 'normativa' ? 'NORMATIVA' : 'MEJORA';
      const cod = o.codigoAnomalia ? ` [${o.codigoAnomalia}]` : '';
      const ref = o.referenciaNormativa ? `\n   Ref: ${o.referenciaNormativa}` : '';
      const just = o.justificacionNormativa ? `\n   "${o.justificacionNormativa}"` : '';
      return `${i + 1}. [${prio}] [${tipo}]${cod} ${o.texto}${ref}${just}`;
    })
    .join('\n');
};


/** Celda de observación editable inline con debounce 500ms. */
const ObservacionesCell: React.FC<{
  value: string;
  onSave: (v: string) => void;
  disabled?: boolean;
}> = ({ value, onSave, disabled }) => {
  const [local, setLocal] = useState(value || '');
  useEffect(() => setLocal(value || ''), [value]);
  useEffect(() => {
    if ((local || '') === (value || '')) return;
    const t = setTimeout(() => onSave(local), 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local]);
  return (
    <Textarea
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      placeholder="Sin observaciones"
      disabled={disabled}
      className="min-h-[44px] text-xs resize-y"
      rows={1}
    />
  );
};

const getEstadoIcon = (resultado: 'ok' | 'anomalia' | 'pendiente') => {
  if (resultado === 'ok') return <CheckCircle className="h-5 w-5 text-green-600" />;
  if (resultado === 'anomalia') return <AlertTriangle className="h-5 w-5 text-orange-500" />;
  return <Clock className="h-5 w-5 text-muted-foreground" />;
};

export const ChecklistRevisionPage: React.FC<ChecklistRevisionPageProps> = ({
  sistemaId, sistemaTipo, clienteId, clienteNombre, checklistId: initialChecklistId, onClose, onSaved,
}) => {
  const { perfil, empresa } = useAuth();
  const { toast } = useToast();

  const template = getChecklistTemplate(sistemaTipo);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [checklistId, setChecklistId] = useState<string | undefined>(initialChecklistId);
  const [tipoRevision, setTipoRevision] = useState<TipoRevision>('trimestral');
  const [numeroTrimestral, setNumeroTrimestral] = useState<1 | 2 | 3 | null>(null);
  const [periodo, setPeriodo] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  // Categoría del sistema (pci vs seguridad): determina si pedimos nº de trimestral.
  const esPci = React.useMemo(() => getSystemCategory(sistemaTipo) === 'pci', [sistemaTipo]);
  const [tipoConfirmado, setTipoConfirmado] = useState<boolean>(!!initialChecklistId);
  const [estado, setEstado] = useState<'en_progreso' | 'completado'>('en_progreso');
  const [observaciones, setObservaciones] = useState<ObservacionItem[]>([]);
  const [respuestas, setRespuestas] = useState<Record<string, RespuestaItem>>({});
  const [cabeceras, setCabeceras] = useState<Record<string, any>>({});
  const [sugerenciaAplicada, setSugerenciaAplicada] = useState<boolean>(false);

  // ---- Elementos del sistema ----
  type ResultadoElemento = 'ok' | 'anomalia' | 'pendiente';
  type ElementoRow = Record<string, any> & { id: string; nombre: string; tipo: string | null };
  interface ElementoEstado {
    id?: string; // row id en checklist_elementos_probados
    resultado: ResultadoElemento;
    observaciones: string;
  }
  const [elementos, setElementos] = useState<ElementoRow[]>([]);
  const [elementoEstados, setElementoEstados] = useState<Record<string, ElementoEstado>>({});

  // Filtros tabla revisión rápida
  const [searchEl, setSearchEl] = useState('');
  const [filtroResultado, setFiltroResultado] = useState<'todos' | ResultadoElemento>('todos');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroMarca, setFiltroMarca] = useState('');
  const [filtroModelo, setFiltroModelo] = useState('');
  const [filtroZona, setFiltroZona] = useState('');
  const [filtroAnomalia, setFiltroAnomalia] = useState('');

  // Añadir / editar / eliminar elementos desde la revisión
  const [addElementoOpen, setAddElementoOpen] = useState(false);
  const [editElementoTarget, setEditElementoTarget] = useState<ElementoRow | null>(null);
  const [eliminarElementoTarget, setEliminarElementoTarget] = useState<ElementoRow | null>(null);
  const [eliminandoElemento, setEliminandoElemento] = useState(false);

  const handleElementoCreado = async (elementoId: string) => {
    const { data } = await (supabase as any)
      .from('elementos')
      .select('*')
      .eq('id', elementoId)
      .single();
    if (data) {
      setElementos((prev) => {
        if (prev.some((e) => e.id === elementoId)) {
          return prev.map((e) => (e.id === elementoId ? (data as ElementoRow) : e));
        }
        return [...prev, data as ElementoRow];
      });
    }
  };

  // Refresca un elemento concreto desde la BD tras editarlo en el diálogo.
  const refrescarElemento = async (elementoId: string) => {
    const { data } = await (supabase as any)
      .from('elementos')
      .select('*')
      .eq('id', elementoId)
      .single();
    if (data) {
      setElementos((prev) => prev.map((e) => (e.id === elementoId ? (data as ElementoRow) : e)));
    }
  };

  const handleEliminarElemento = async () => {
    if (!eliminarElementoTarget) return;
    setEliminandoElemento(true);
    try {
      const elId = eliminarElementoTarget.id;
      // Borrar primero la fila de checklist_elementos_probados si existe
      if (checklistId) {
        await supabase.from('checklist_elementos_probados').delete().eq('checklist_id', checklistId).eq('elemento_id', elId);
      }
      const { error } = await supabase.from('elementos').delete().eq('id', elId);
      if (error) throw error;
      setElementos((prev) => prev.filter((e) => e.id !== elId));
      setElementoEstados((prev) => {
        const { [elId]: _, ...rest } = prev;
        return rest;
      });
      toast({ title: 'Elemento eliminado', description: 'Se ha quitado del sistema.' });
      setEliminarElementoTarget(null);
    } catch (e: any) {
      console.error(e);
      toast({ title: 'Error', description: e.message || 'No se pudo eliminar el elemento.', variant: 'destructive' });
    } finally {
      setEliminandoElemento(false);
    }
  };

  // Anomalías
  const { anomaliasElementos, crearAnomalia, cargarAnomaliasElementos, resolverAnomalia, loading: loadingAnom } = useAnomalias();
  const [anomaliaDialog, setAnomaliaDialog] = useState<{ elemento: ElementoRow } | null>(null);
  const [descripcionAnomalia, setDescripcionAnomalia] = useState('');
  useEffect(() => { if (sistemaId) cargarAnomaliasElementos(); /* eslint-disable-next-line */ }, [sistemaId]);

  // ---- Estado de guardado / pendientes ----
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [dirty, setDirty] = useState(false);
  const [confirmFinalizar, setConfirmFinalizar] = useState<{ pendientes: number } | null>(null);

  const autosaveTimer = useRef<ReturnType<typeof setTimeout>>();
  const isReadOnly = estado === 'completado';


  const parseObservaciones = (raw: string | null | undefined): ObservacionItem[] => {
    if (!raw) return [];
    try {
      const j = JSON.parse(raw);
      if (Array.isArray(j)) return j as ObservacionItem[];
    } catch { /* legacy texto plano */ }
    return raw.trim()
      ? [{ id: crypto.randomUUID(), prioridad: 'media', tipo: 'mejora', texto: raw }]
      : [];
  };
  const observacionesToText = (): string => {
    if (!observaciones.length) return '';
    return JSON.stringify(observaciones);
  };
  const addObservacion = () => {
    setObservaciones(prev => [...prev, { id: crypto.randomUUID(), prioridad: 'media', tipo: 'mejora', texto: '', fotos: [] }]);
    scheduleAutosave();
  };
  const updateObservacion = (id: string, patch: Partial<ObservacionItem>) => {
    setObservaciones(prev => prev.map(o => o.id === id ? { ...o, ...patch } : o));
    scheduleAutosave();
  };
  const removeObservacion = (id: string) => {
    setObservaciones(prev => prev.filter(o => o.id !== id));
    scheduleAutosave();
  };

  const uploadFotosObservacion = async (obsId: string, files: FileList | null) => {
    if (!files || files.length === 0) return;
    const cid = await ensureChecklist();
    if (!cid) return;
    const nuevas: string[] = [];
    for (const file of Array.from(files)) {
      const ext = file.name.split('.').pop() || 'jpg';
      const path = `${cid}/${obsId}/${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage
        .from('checklist-documentos')
        .upload(path, file, { upsert: false, contentType: file.type });
      if (error) {
        toast({ title: 'Error subiendo foto', description: error.message, variant: 'destructive' });
        continue;
      }
      nuevas.push(path);
    }
    if (nuevas.length) {
      setObservaciones(prev => prev.map(o =>
        o.id === obsId ? { ...o, fotos: [...(o.fotos || []), ...nuevas] } : o
      ));
      scheduleAutosave();
    }
  };
  const removeFotoObservacion = async (obsId: string, path: string) => {
    await supabase.storage.from('checklist-documentos').remove([path]);
    setObservaciones(prev => prev.map(o =>
      o.id === obsId ? { ...o, fotos: (o.fotos || []).filter(f => f !== path) } : o
    ));
    scheduleAutosave();
  };
  const [fotoUrls, setFotoUrls] = useState<Record<string, string>>({});
  const ensureFotoUrl = async (path: string) => {
    if (fotoUrls[path]) return;
    const { data } = await supabase.storage.from('checklist-documentos').createSignedUrl(path, 3600);
    if (data?.signedUrl) {
      setFotoUrls(prev => ({ ...prev, [path]: data.signedUrl }));
    }
  };

  // ---- Cargar / inicializar ----
  useEffect(() => {

    const load = async () => {
      setLoading(true);
      try {
        let cid = initialChecklistId;
        if (!cid && perfil) {
          const { data: existing } = await (supabase as any)
            .from('checklists_seguridad')
            .select('id, tipo_revision, estado, observaciones_generales, firma_tecnico, periodo, numero_trimestral')
            .eq('cliente_id', clienteId)
            .eq('sistema_id', sistemaId)
            .eq('tecnico_id', perfil.user_id)
            .eq('estado', 'en_progreso')
            .eq('modo', 'cuestionario')
            .eq('archivado', false)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          if (existing) {
            cid = existing.id;
            setTipoRevision((existing.tipo_revision as TipoRevision) || 'trimestral');
            if (existing.periodo) setPeriodo(existing.periodo);
            setNumeroTrimestral((existing.numero_trimestral as 1 | 2 | 3 | null) ?? null);
            setObservaciones(parseObservaciones(existing.observaciones_generales));
            // firma del técnico ya no se gestiona en el checklist (vive en el informe consolidado)

            setEstado(existing.estado as any);
            setSugerenciaAplicada(false);
          } else {
            // Nuevo checklist: sugerir tipo/nº/periodo según historial del sistema.
            const { data: historial } = await (supabase as any)
              .from('checklists_seguridad')
              .select('fecha, periodo, tipo_revision, estado, numero_trimestral')
              .eq('sistema_id', sistemaId)
              .in('estado', ['completado', 'finalizada'])
              .eq('archivado', false);
            const sugerencia = sugerirProximaRevision(sistemaTipo, (historial || []) as any);
            setTipoRevision(sugerencia.tipoRevision as TipoRevision);
            setNumeroTrimestral(sugerencia.numeroTrimestral);
            setPeriodo(sugerencia.periodo);
            setSugerenciaAplicada(true);
          }
        } else if (cid) {
          const { data: existing } = await (supabase as any)
            .from('checklists_seguridad')
            .select('tipo_revision, estado, observaciones_generales, firma_tecnico, periodo, numero_trimestral')
            .eq('id', cid)
            .maybeSingle();
          if (existing) {
            setTipoRevision((existing.tipo_revision as TipoRevision) || 'trimestral');
            if (existing.periodo) setPeriodo(existing.periodo);
            setNumeroTrimestral((existing.numero_trimestral as 1 | 2 | 3 | null) ?? null);
            setObservaciones(parseObservaciones(existing.observaciones_generales));
            // firma del técnico ya no se gestiona en el checklist (vive en el informe consolidado)
            setEstado(existing.estado as any);
            setSugerenciaAplicada(false);
          }
        }
        setChecklistId(cid);
        setTipoConfirmado(!!cid || !!initialChecklistId);


        if (cid) {
          const { data: resp } = await supabase
            .from('checklist_respuestas')
            .select('pregunta_id, respuesta, observaciones')
            .eq('checklist_id', cid);
          const r: Record<string, RespuestaItem> = {};
          (resp || []).forEach((x: any) => {
            r[x.pregunta_id] = { respuesta: x.respuesta as Respuesta, observaciones: x.observaciones || '' };
          });
          setRespuestas(r);

          const { data: cabs } = await (supabase as any)
            .from('checklist_cabeceras')
            .select('protocolo_id, datos')
            .eq('checklist_id', cid);
          const cmap: Record<string, any> = {};
          (cabs || []).forEach((x: any) => { cmap[x.protocolo_id] = x.datos; });
          setCabeceras(cmap);
        } else {
          setRespuestas({});
          setCabeceras({});
        }

        // Cargar elementos del sistema (siempre) y estados ya guardados (si hay checklist).
        const { data: elems } = await (supabase as any)
          .from('elementos')
          .select('*')
          .eq('sistema_id', sistemaId)
          .order('numero_elemento', { ascending: true, nullsFirst: false })
          .order('nombre', { ascending: true });
        setElementos((elems || []) as ElementoRow[]);

        if (cid) {
          const { data: probados } = await supabase
            .from('checklist_elementos_probados')
            .select('id, elemento_id, resultado, observaciones')
            .eq('checklist_id', cid);
          const m: Record<string, ElementoEstado> = {};
          (probados || []).forEach((x: any) => {
            m[x.elemento_id] = {
              id: x.id,
              resultado: (x.resultado as ResultadoElemento) || 'pendiente',
              observaciones: x.observaciones || '',
            };
          });
          setElementoEstados(m);
        } else {
          setElementoEstados({});
        }
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
  }, [sistemaId, sistemaTipo, initialChecklistId, perfil?.user_id]);

  // Flush pendiente al cerrar pestaña/ocultar
  useEffect(() => {
    const flush = () => { if (dirty && !isReadOnly) { void guardarBorrador(true); } };
    const onVis = () => { if (document.visibilityState === 'hidden') flush(); };
    window.addEventListener('beforeunload', flush);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('beforeunload', flush);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [dirty, isReadOnly]);

  const ensureChecklist = async (): Promise<string | null> => {
    if (checklistId) return checklistId;
    if (!perfil || !empresa) return null;

    // Reuse existing checklist for the same (cliente, sistema, tipo_revision, periodo)
    // para garantizar UN SOLO checklist por sistema/revisión/periodo.
    const { data: existente } = await (supabase as any)
      .from('checklists_seguridad')
      .select('id, estado')
      .eq('cliente_id', clienteId)
      .eq('sistema_id', sistemaId)
      .eq('tipo_revision', tipoRevision)
      .eq('periodo', periodo)
      .eq('modo', 'cuestionario')
      .eq('archivado', false)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (existente?.id) {
      setChecklistId(existente.id);
      if (existente.estado === 'completado') {
        await supabase
          .from('checklists_seguridad')
          .update({ estado: 'en_progreso' } as any)
          .eq('id', existente.id);
        setEstado('en_progreso');
      }
      return existente.id;
    }

    const { data, error } = await supabase
      .from('checklists_seguridad')
      .insert({
        cliente_id: clienteId,
        sistema_id: sistemaId,
        tecnico_id: perfil.user_id,
        empresa_id: empresa.id,
        estado: 'en_progreso',
        tipo_revision: tipoRevision,
        modo: 'cuestionario',
        archivado: false,
        periodo,
        numero_trimestral: esPci && tipoRevision === 'trimestral' ? numeroTrimestral : null,
      } as any)
      .select('id')
      .single();
    if (error || !data) {
      toast({ title: 'No se pudo crear el borrador', description: error?.message, variant: 'destructive' });
      return null;
    }
    setChecklistId(data.id);
    return data.id;
  };


  const guardarBorrador = async (silent = false): Promise<boolean> => {
    if (isReadOnly) return true;
    setSaving(true);
    try {
      const cid = await ensureChecklist();
      if (!cid) return false;
      const { error: checklistUpdateError } = await supabase
        .from('checklists_seguridad')
        .update({
          tipo_revision: tipoRevision,
          periodo,
          numero_trimestral: esPci && tipoRevision === 'trimestral' ? numeroTrimestral : null,
          observaciones_generales: observacionesToText() || null,
        } as any)

        .eq('id', cid);
      if (checklistUpdateError) throw checklistUpdateError;

      const respRows = Object.entries(respuestas)
        .filter(([, v]) => v.respuesta || v.observaciones)
        .map(([pregunta_id, v]) => {
          const protocolo = template?.protocolos.find(p => p.preguntas.some(q => q.id === pregunta_id));
          const preg = protocolo?.preguntas.find(q => q.id === pregunta_id);
          return {
            checklist_id: cid,
            bloque_id: protocolo?.id || 'general',
            protocolo_id: protocolo?.id || 'general',
            pregunta_id,
            pregunta_texto: preg?.texto || pregunta_id,
            respuesta: v.respuesta || null,
            observaciones: v.observaciones || null,
            fotos: '[]',
          };
        });
      if (respRows.length) {
        const { error: respError } = await supabase
          .from('checklist_respuestas')
          .upsert(respRows, { onConflict: 'checklist_id,pregunta_id' });
        if (respError) throw respError;
      }

      // Upsert cabeceras (bloques con datos especiales como prueba de prestaciones)
      const cabRows = Object.entries(cabeceras)
        .filter(([, v]) => v != null)
        .map(([protocolo_id, datos]) => ({ checklist_id: cid, protocolo_id, datos }));
      if (cabRows.length) {
        const { error: cabError } = await (supabase as any)
          .from('checklist_cabeceras')
          .upsert(cabRows, { onConflict: 'checklist_id,protocolo_id' });
        if (cabError) throw cabError;
      }

      // Upsert elementos probados (solo los que tienen estado distinto a 'pendiente' o con observaciones)
      const elemRows = Object.entries(elementoEstados)
        .filter(([, v]) => v.resultado !== 'pendiente' || (v.observaciones && v.observaciones.trim()))
        .map(([elemento_id, v]) => ({
          checklist_id: cid,
          elemento_id,
          resultado: v.resultado,
          observaciones: v.observaciones?.trim() ? v.observaciones : null,
        }));
      if (elemRows.length) {
        const { error: elemError } = await supabase
          .from('checklist_elementos_probados')
          .upsert(elemRows, { onConflict: 'checklist_id,elemento_id' });
        if (elemError) throw elemError;
      }

      setDirty(false);
      setSavedAt(new Date());
      if (!silent) toast({ title: 'Borrador guardado', description: 'Puedes continuar más tarde.' });
      return true;
    } catch (e: any) {
      toast({ title: 'Error guardando borrador', description: e?.message, variant: 'destructive' });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const scheduleAutosave = () => {
    if (isReadOnly) return;
    setDirty(true);
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => { guardarBorrador(true); }, 2500);
  };

  const setElementoEstado = (elementoId: string, patch: Partial<ElementoEstado>) => {
    setElementoEstados(prev => ({
      ...prev,
      [elementoId]: {
        resultado: patch.resultado ?? prev[elementoId]?.resultado ?? 'pendiente',
        observaciones: patch.observaciones !== undefined ? patch.observaciones : (prev[elementoId]?.observaciones ?? ''),
        id: prev[elementoId]?.id,
      },
    }));
    scheduleAutosave();
  };

  const elementosStats = useMemo(() => {
    let ok = 0, anom = 0, pend = 0;
    elementos.forEach(e => {
      const r = elementoEstados[e.id]?.resultado ?? 'pendiente';
      if (r === 'ok') ok++;
      else if (r === 'anomalia') anom++;
      else pend++;
    });
    return { ok, anom, pend, total: elementos.length };
  }, [elementos, elementoEstados]);

  // --- Tabla revisión rápida: columnas dinámicas, filtros derivados ---
  const allowedColumnIds = useMemo(() => {
    const allowed = new Set<string>();
    for (const col of ELEMENTO_COLUMNS) if (!col.id.startsWith('dt:')) allowed.add(col.id);
    const tipos = new Set(elementos.map((e) => e.tipo).filter(Boolean) as string[]);
    for (const tipo of tipos) for (const f of getFieldsForTipo(tipo)) allowed.add(`dt:${f.key}`);
    return allowed;
  }, [elementos]);
  const { visibleColumns } = useElementoColumnPrefs({ allowedIds: allowedColumnIds });
  const columnasFinales = useMemo(() => {
    const obsCol = ELEMENTO_COLUMNS.find((c) => c.id === 'observaciones');
    if (!obsCol) return visibleColumns;
    if (visibleColumns.some((c) => c.id === 'observaciones')) return visibleColumns;
    return [...visibleColumns, obsCol];
  }, [visibleColumns]);

  const marcasDisponibles = useMemo(() => Array.from(new Set(elementos.map(e => e.marca).filter(Boolean))) as string[], [elementos]);
  const modelosDisponibles = useMemo(() => Array.from(new Set(elementos.map(e => e.modelo).filter(Boolean))) as string[], [elementos]);
  const zonasDisponibles = useMemo(() => Array.from(new Set(elementos.map(e => e.zona).filter(Boolean))) as string[], [elementos]);
  const tiposDisponibles = useMemo(() => Array.from(new Set(elementos.map(e => e.tipo).filter(Boolean))) as string[], [elementos]);
  const anomaliasDisponibles = useMemo(() => {
    const map = new Map<string, string>();
    for (const el of elementos) for (const a of anomaliasElementos) {
      if (a.elemento_id === el.id && a.codigo_anomalia) {
        map.set(a.codigo_anomalia, a.codigo_info?.descripcion || a.codigo_anomalia);
      }
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [elementos, anomaliasElementos]);

  const elementosFiltrados = useMemo(() => {
    const term = searchEl.toLowerCase();
    return elementos.filter((el) => {
      let matchSearch = true;
      if (term) {
        const fields = [el.nombre, el.tipo, el.zona, el.marca, el.modelo, el.mac_serie, el.anio_fabricacion, el.observaciones, el.estado, el.numero_elemento != null ? String(el.numero_elemento) : '', el.fecha_instalacion];
        const native = fields.some(v => String(v || '').toLowerCase().includes(term));
        if (!native) {
          const dt = el.datos_tecnicos || {};
          const dtMatch = Object.values(dt).some(v => String(v || '').toLowerCase().includes(term));
          if (!dtMatch) matchSearch = false;
        }
      }
      const r = elementoEstados[el.id]?.resultado ?? 'pendiente';
      const matchRes = filtroResultado === 'todos' || r === filtroResultado;
      const matchTipo = !filtroTipo || el.tipo === filtroTipo;
      const matchAnom = !filtroAnomalia || anomaliasElementos.some(a => a.elemento_id === el.id && a.codigo_anomalia === filtroAnomalia);
      const matchMarca = !filtroMarca || el.marca === filtroMarca;
      const matchModelo = !filtroModelo || el.modelo === filtroModelo;
      const matchZona = !filtroZona || el.zona === filtroZona;
      return matchSearch && matchRes && matchTipo && matchAnom && matchMarca && matchModelo && matchZona;
    });
  }, [elementos, searchEl, filtroResultado, elementoEstados, filtroTipo, filtroAnomalia, anomaliasElementos, filtroMarca, filtroModelo, filtroZona]);

  const tieneAnomaliaAbierta = (elementoId: string) => anomaliasElementos.some((a) => a.elemento_id === elementoId && !a.resuelta);

  const handleMarcarOk = (elemento: ElementoRow) => {
    const actual = elementoEstados[elemento.id]?.resultado ?? 'pendiente';
    if (actual === 'ok' || actual === 'anomalia') {
      setElementoEstado(elemento.id, { resultado: 'pendiente' });
    } else {
      const destino: ResultadoElemento = tieneAnomaliaAbierta(elemento.id) ? 'anomalia' : 'ok';
      setElementoEstado(elemento.id, { resultado: destino });
    }
  };

  const registrarAnomalia = async (codigo: CodigoAnomalia) => {
    if (!anomaliaDialog) return;
    try {
      await crearAnomalia(anomaliaDialog.elemento.id, codigo.codigo, descripcionAnomalia || undefined);
      setElementoEstado(anomaliaDialog.elemento.id, { resultado: 'anomalia', observaciones: descripcionAnomalia || elementoEstados[anomaliaDialog.elemento.id]?.observaciones || '' });
      setAnomaliaDialog(null);
      setDescripcionAnomalia('');
    } catch (e) { console.error(e); }
  };

  const handleResolverAnomalia = async (elementoId: string, anomaliaId: string) => {
    await resolverAnomalia(anomaliaId);
    const refrescadas = await cargarAnomaliasElementos();
    const quedan = (refrescadas || []).some((a) => a.elemento_id === elementoId && !a.resuelta);
    if (!quedan) setElementoEstado(elementoId, { resultado: 'pendiente' });
  };


  const finalizarConfirmado = async () => {
    setFinalizing(true);
    try {
      const guardado = await guardarBorrador(true);
      if (!guardado) return;
      const cid = await ensureChecklist();
      if (!cid) return;
      const { error: finalizarError } = await supabase
        .from('checklists_seguridad')
        .update({ estado: 'completado' } as any)
        .eq('id', cid);
      if (finalizarError) throw finalizarError;
      setEstado('completado');

      // Marcar elementos OK/anomalía como revisado=true
      const ids = Object.entries(elementoEstados)
        .filter(([, v]) => v.resultado === 'ok' || v.resultado === 'anomalia')
        .map(([id]) => id);
      if (ids.length) {
        await (supabase as any)
          .from('elementos')
          .update({ revisado: true })
          .in('id', ids);
      }

      toast({ title: 'Revisión finalizada', description: 'Checklist guardado. Genera el informe consolidado desde el botón "Generar informe".' });
      onSaved?.();
      onClose();
    } catch (e: any) {
      toast({ title: 'Error finalizando', description: e?.message, variant: 'destructive' });
    } finally {
      setFinalizing(false);
      setConfirmFinalizar(null);
    }
  };

  const finalizar = async () => {
    if (elementosStats.pend > 0) {
      setConfirmFinalizar({ pendientes: elementosStats.pend });
      return;
    }
    await finalizarConfirmado();
  };

  const handleClose = () => {
    if (dirty && !isReadOnly) {
      void guardarBorrador(true);
    }
    onClose();
  };

  const protocolos = template?.protocolos || [];

  const totalPreguntas = useMemo(() =>
    protocolos.reduce((acc, p) => acc + filtrarPorTipoRevision(p.preguntas, tipoRevision).length, 0)
  , [protocolos, tipoRevision]);
  const respondidas = useMemo(() => {
    let count = 0;
    protocolos.forEach(p => {
      filtrarPorTipoRevision(p.preguntas, tipoRevision).forEach(q => {
        if (respuestas[q.id]?.respuesta) count++;
      });
    });
    return count;
  }, [protocolos, respuestas, tipoRevision]);

  if (!template) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 p-4 md:p-6">
        <div className="max-w-3xl mx-auto rounded-lg border bg-card p-6 space-y-3">
          <Button variant="ghost" onClick={onClose} className="gap-2"><ArrowLeft className="h-4 w-4" /> Volver</Button>
          <h2 className="text-lg font-semibold">Plantilla no disponible</h2>
          <p className="text-sm text-muted-foreground">Aún no hay una plantilla de revisión para este tipo de sistema.</p>
        </div>
      </div>
    );
  }

  const setRespuesta = (id: string, patch: Partial<RespuestaItem>) => {
    setRespuestas(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));
    scheduleAutosave();
  };

  const setRespuestaBloque = (preguntaIds: string[], respuesta: Respuesta) => {
    setRespuestas(prev => {
      const next = { ...prev };
      preguntaIds.forEach(id => {
        next[id] = { ...next[id], respuesta };
      });
      return next;
    });
    scheduleAutosave();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-4">
        <div className="flex items-center gap-3 flex-wrap rounded-lg border bg-card px-4 py-3 shadow-sm">
          <Button variant="ghost" size="sm" onClick={handleClose} className="gap-2">
            <ArrowLeft className="h-4 w-4" /> Volver
          </Button>
          <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
            <FileText className="h-5 w-5 shrink-0" />
            <h1 className="text-lg font-semibold truncate">Revisión · {template.titulo}</h1>
            <Badge variant="secondary" className="capitalize">{TIPO_REVISION_LABEL[tipoRevision]}</Badge>
            {estado === 'completado'
              ? <Badge className="bg-green-500/20 text-green-600 border-green-500/30">Completado</Badge>
              : <Badge className="bg-yellow-500/20 text-yellow-600 border-yellow-500/30">Borrador</Badge>}
            <span className="ml-auto text-xs text-muted-foreground">{respondidas} / {totalPreguntas} preguntas</span>
          </div>
        </div>


        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin h-6 w-6" /></div>
        ) : !tipoConfirmado ? (
          // Pantalla previa: el técnico debe elegir el tipo de revisión antes de empezar
          <div className="rounded-lg border bg-card p-6">
            <div className="max-w-md mx-auto space-y-4">
              <p className="text-sm text-muted-foreground">
                Indica el <strong>periodo</strong> al que corresponde la revisión y el <strong>tipo</strong> a realizar. El periodo permite emparejar este checklist con la revisión rápida del mismo tipo y periodo.
              </p>
              {sugerenciaAplicada && (
                <div className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-foreground">
                  Sugerencia calculada a partir de la última revisión del sistema. Puedes modificar tipo, nº trimestral o periodo antes de empezar.
                </div>
              )}
              <div className="space-y-2">
                <Label>Periodo (MM-AAAA)</Label>
                <input
                  type="month"
                  value={periodo}
                  onChange={(e) => setPeriodo(e.target.value)}
                  className="w-full h-11 rounded-md border border-input bg-background px-3 text-base"
                />
                <p className="text-[11px] text-muted-foreground">
                  Mes/año al que pertenece la revisión, independientemente de la fecha real de ejecución.
                </p>
              </div>
              <div className="space-y-2">
                <Label>Tipo de revisión</Label>
                <div className="grid gap-2">
                  {(['trimestral', 'semestral', 'anual'] as TipoRevision[]).map((t) => (
                    <Button
                      key={t}
                      variant={tipoRevision === t ? 'default' : 'outline'}
                      className="justify-start h-auto py-3"
                      onClick={() => setTipoRevision(t)}
                    >
                      <div className="text-left">
                        <div className="font-medium capitalize">{TIPO_REVISION_LABEL[t]}</div>
                        <div className="text-xs opacity-80">
                          {t === 'trimestral' && 'Comprobaciones trimestrales (T)'}
                          {t === 'semestral' && 'Comprobaciones semestrales (S)'}
                          {t === 'anual' && 'Revisión anual completa (T + S + A)'}
                        </div>
                      </div>
                    </Button>
                  ))}
                </div>
              </div>
              {esPci && tipoRevision === 'trimestral' && (
                <div className="space-y-2">
                  <Label>¿Qué trimestral del ciclo es?</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {([1, 2, 3] as const).map((n) => (
                      <Button
                        key={n}
                        variant={numeroTrimestral === n ? 'default' : 'outline'}
                        className="h-12"
                        onClick={() => setNumeroTrimestral(n)}
                      >
                        {n}ª Trimestral ({n}T)
                      </Button>
                    ))}
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Ciclo PCI: A → 1T → 2T → 3T → A. Indica qué posición ocupa esta revisión para calcular la próxima correctamente.
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={onClose}>Cancelar</Button>
                <Button
                  disabled={
                    !/^\d{4}-\d{2}$/.test(periodo) ||
                    (esPci && tipoRevision === 'trimestral' && !numeroTrimestral)
                  }
                  onClick={() => setTipoConfirmado(true)}
                >
                  Empezar revisión
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6 pb-24">
            {/* Periodo (editable) + Tipo de revisión (bloqueado) */}
            <div className="flex flex-col sm:flex-row sm:items-end gap-3 rounded-lg border bg-muted/30 p-3">
              <div className="space-y-1 flex-1 min-w-[180px]">
                <Label className="text-xs">Periodo (MM-AAAA)</Label>
                <input
                  type="month"
                  value={periodo}
                  onChange={(e) => {
                    setPeriodo(e.target.value);
                    scheduleAutosave();
                  }}
                  disabled={isReadOnly}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-base font-mono"
                />
                <p className="text-[10px] text-muted-foreground">
                  Mes/año al que pertenece la revisión, independientemente de la fecha real de ejecución. Puedes modificarlo.
                </p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Tipo de revisión</Label>
                <Select value={tipoRevision} disabled>
                  <SelectTrigger className="w-[180px] h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trimestral">Trimestral (T)</SelectItem>
                    <SelectItem value="semestral">Semestral (S)</SelectItem>
                    <SelectItem value="anual">Anual (T + S + A)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground">El tipo no puede modificarse.</p>
              </div>
              {esPci && tipoRevision === 'trimestral' && (
                <div className="space-y-1">
                  <Label className="text-xs">Nº trimestral (ciclo PCI)</Label>
                  <Select
                    value={numeroTrimestral ? String(numeroTrimestral) : ''}
                    onValueChange={(v) => {
                      setNumeroTrimestral(Number(v) as 1 | 2 | 3);
                      scheduleAutosave();
                    }}
                    disabled={isReadOnly}
                  >
                    <SelectTrigger className="w-[160px] h-10">
                      <SelectValue placeholder="1T / 2T / 3T" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1ª Trimestral (1T)</SelectItem>
                      <SelectItem value="2">2ª Trimestral (2T)</SelectItem>
                      <SelectItem value="3">3ª Trimestral (3T)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground">Posición en el ciclo A→1T→2T→3T→A.</p>
                </div>
              )}
            </div>


            {/* SECCIÓN PLEGABLE: Checklist de preguntas */}
            <Collapsible defaultOpen className="rounded-lg border bg-card shadow-sm">
              <CollapsibleTrigger className="w-full flex items-center gap-2 px-4 py-3 border-b hover:bg-muted/40 transition-colors [&[data-state=open]>svg]:rotate-180">
                <ClipboardCheck className="h-4 w-4 text-primary" />
                <span className="font-semibold text-sm flex-1 text-left">Checklist de preguntas</span>
                <Badge variant="outline" className="text-xs">{respondidas} / {totalPreguntas}</Badge>
                <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
              </CollapsibleTrigger>
              <CollapsibleContent className="p-3 space-y-4">
                {/* Preguntas por protocolo */}
                {protocolos.map(p => {
                  // Bloques con render especial (ej. curva caudal/presión)
                  if (p.tipo_render === 'curva_caudal') {
                    if (p.periodicidad && !p.periodicidad.includes(tipoRevision === 'trimestral' ? 'T' : tipoRevision === 'semestral' ? 'S' : 'A')) return null;
                    const val: PruebaPrestacionesValue = normalizePruebaPrestaciones(cabeceras[p.id]);
                    const bombasInventario: BombaInventario[] = elementos
                      .map(el => {
                        const t = (el.tipo || '').toString().toLowerCase();
                        const norm = t.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                        let tipo: TipoBomba | null = null;
                        if (norm.includes('bomba principal electrica')) tipo = 'electrica';
                        else if (norm.includes('bomba principal diesel') || norm === 'bomba diesel' || norm.includes('bomba diesel')) tipo = 'diesel';
                        if (!tipo) return null;
                        const dt = el.datos_tecnicos || {};
                        return {
                          id: el.id,
                          nombre: (el.nombre || el.tipo || 'Bomba').toString(),
                          tipo,
                          nominalCaudal: Number(dt.bomba_caudal) || undefined,
                          nominalPresion: Number(dt.bomba_altura) || undefined,
                          unidadCaudalPlaca: parseUnidadCaudal(dt.bomba_caudal_unidad),
                          unidadPresionPlaca: parseUnidadPresion(dt.presion_unidad),
                        } as BombaInventario;
                      })
                      .filter((b): b is BombaInventario => b !== null);

                    // Resolver instrumentos compartidos del sistema.
                    const instrumentos: InstrumentosSala = resolveInstrumentosGrupoPresion(elementos);

                    return (
                      <div key={p.id} className="rounded-lg border">
                        <div className="px-4 py-2 bg-muted/40 border-b">
                          <span className="font-semibold text-sm">{p.titulo}</span>
                        </div>
                        <div className="p-3">
                          <PruebaPrestacionesBlock
                            value={val}
                            bombasInventario={bombasInventario}
                            instrumentos={instrumentos}
                            onChange={(v) => {
                              setCabeceras(prev => ({ ...prev, [p.id]: v }));
                              scheduleAutosave();
                            }}
                            disabled={isReadOnly}
                          />
                        </div>
                      </div>
                    );
                  }
                  const preguntas = filtrarPorTipoRevision(p.preguntas, tipoRevision);
                  if (preguntas.length === 0) return null;
                  return (
                    <div key={p.id} className="rounded-lg border">
                      <div className="px-4 py-2 bg-muted/40 border-b flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-sm flex-1">{p.titulo}</span>
                        {!isReadOnly && (
                          <div className="flex items-center gap-1">
                            <span className="text-[10px] text-muted-foreground mr-1">Marcar todas:</span>
                            {(['si','no','na'] as Respuesta[]).map(opt => (
                              <Button
                                key={opt}
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs"
                                onClick={() => setRespuestaBloque(preguntas.map(q => q.id), opt)}
                              >
                                {opt === 'si' ? 'SÍ' : opt === 'no' ? 'NO' : 'N/A'}
                              </Button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="divide-y">
                        {preguntas.map(q => {
                          const r = respuestas[q.id] || {};
                          return (
                            <div key={q.id} className="p-3 space-y-2">
                              <div className="flex items-start gap-2">
                                <Badge variant="outline" className="text-[10px] font-mono shrink-0">{q.periodicidad.join('/')}</Badge>
                                <span className="text-[11px] font-mono text-muted-foreground shrink-0">{q.id}</span>
                                <p className="text-sm flex-1">{q.texto}</p>
                              </div>
                              <div className="flex flex-wrap items-center gap-2 ml-12">
                                {(['si','no','na'] as Respuesta[]).map(opt => (
                                  <Button
                                    key={opt}
                                    size="sm"
                                    variant={r.respuesta === opt ? 'default' : 'outline'}
                                    onClick={() => setRespuesta(q.id, { respuesta: opt })}
                                    disabled={isReadOnly}
                                    className="h-8 px-3"
                                  >
                                    {opt === 'si' ? 'SÍ' : opt === 'no' ? 'NO' : 'N/A'}
                                  </Button>
                                ))}
                                <Input
                                  placeholder="Observaciones (opcional)"
                                  value={r.observaciones || ''}
                                  onChange={(e) => setRespuesta(q.id, { observaciones: e.target.value })}
                                  disabled={isReadOnly}
                                  className="h-8 flex-1 min-w-[200px]"
                                />
                              </div>
                              {q.nota && <p className="text-[10px] text-muted-foreground ml-12">{q.nota}</p>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </CollapsibleContent>
            </Collapsible>


            {/* SECCIÓN PLEGABLE: Elementos del sistema probados */}
            <Collapsible defaultOpen className="rounded-lg border bg-card shadow-sm">
              <CollapsibleTrigger className="w-full flex items-center gap-2 px-4 py-3 border-b hover:bg-muted/40 transition-colors [&[data-state=open]>svg]:rotate-180">
                <ListChecks className="h-4 w-4 text-primary" />
                <span className="font-semibold text-sm flex-1 text-left">Elementos del sistema probados</span>
                <div className="flex items-center gap-1 text-xs">
                  <Badge className="bg-green-500/20 text-green-700 border-green-500/30">{elementosStats.ok} OK</Badge>
                  <Badge className="bg-orange-500/20 text-orange-700 border-orange-500/30">{elementosStats.anom} anomalía</Badge>
                  <Badge variant="outline">{elementosStats.pend} pend.</Badge>
                  <span className="text-muted-foreground ml-1">/ {elementosStats.total}</span>
                </div>
                <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
              </CollapsibleTrigger>
              <CollapsibleContent className="p-3 space-y-3">
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setAddElementoOpen(true)}
                    disabled={isReadOnly}
                    className="h-8"
                  >
                    <Plus className="h-4 w-4 mr-1" /> Añadir elemento
                  </Button>
                </div>
                {elementos.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">Este sistema no tiene elementos registrados. Pulsa “Añadir elemento” para crear uno.</p>
                ) : (
                  <>
                    {/* Filtros */}
                    <div className="space-y-2">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Buscar (nombre, tipo, ubicación, marca, modelo, serie, año, observaciones, datos técnicos...)"
                          value={searchEl}
                          onChange={(e) => setSearchEl(e.target.value)}
                          className="pl-10 h-9"
                        />
                        {searchEl && (
                          <Button variant="ghost" size="sm" className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0" onClick={() => setSearchEl('')}>
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Select value={filtroResultado} onValueChange={(v) => setFiltroResultado(v as any)}>
                          <SelectTrigger className="h-9 w-[160px]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="todos">Todos</SelectItem>
                            <SelectItem value="pendiente">Pendientes</SelectItem>
                            <SelectItem value="ok">OK</SelectItem>
                            <SelectItem value="anomalia">Con anomalía</SelectItem>
                          </SelectContent>
                        </Select>
                        {tiposDisponibles.length > 0 && (
                          <Select value={filtroTipo || 'all'} onValueChange={(v) => setFiltroTipo(v === 'all' ? '' : v)}>
                            <SelectTrigger className="h-9 w-[160px]"><SelectValue placeholder="Tipo..." /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos los tipos</SelectItem>
                              {tiposDisponibles.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                        {marcasDisponibles.length > 0 && (
                          <Select value={filtroMarca || 'all'} onValueChange={(v) => setFiltroMarca(v === 'all' ? '' : v)}>
                            <SelectTrigger className="h-9 w-[150px]"><SelectValue placeholder="Marca..." /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todas las marcas</SelectItem>
                              {marcasDisponibles.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                        {modelosDisponibles.length > 0 && (
                          <Select value={filtroModelo || 'all'} onValueChange={(v) => setFiltroModelo(v === 'all' ? '' : v)}>
                            <SelectTrigger className="h-9 w-[150px]"><SelectValue placeholder="Modelo..." /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos los modelos</SelectItem>
                              {modelosDisponibles.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                        {zonasDisponibles.length > 0 && (
                          <Select value={filtroZona || 'all'} onValueChange={(v) => setFiltroZona(v === 'all' ? '' : v)}>
                            <SelectTrigger className="h-9 w-[150px]"><SelectValue placeholder="Ubicación..." /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todas las ubicaciones</SelectItem>
                              {zonasDisponibles.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                        {anomaliasDisponibles.length > 0 && (
                          <Select value={filtroAnomalia || 'all'} onValueChange={(v) => setFiltroAnomalia(v === 'all' ? '' : v)}>
                            <SelectTrigger className="h-9 w-[220px]"><SelectValue placeholder="Anomalía..." /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todas las anomalías</SelectItem>
                              {anomaliasDisponibles.map(([codigo, desc]) => (
                                <SelectItem key={codigo} value={codigo}>{codigo} — {desc}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        <div className="flex items-center gap-2 ml-auto">
                          <ColumnasElementosPicker allowedIds={allowedColumnIds} />
                          <Button variant="ghost" size="sm" className="h-9" onClick={() => {
                            setSearchEl(''); setFiltroResultado('todos'); setFiltroAnomalia('');
                            setFiltroTipo(''); setFiltroMarca(''); setFiltroModelo(''); setFiltroZona('');
                          }}>Limpiar</Button>
                        </div>
                      </div>
                    </div>

                    {/* Tabla */}
                    <div className="w-full max-w-full overflow-x-auto">
                      <Table className="w-full table-fixed text-xs">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-10 px-2"></TableHead>
                            {columnasFinales.map((col) => (
                              <TableHead key={col.id} className="px-2 break-words whitespace-normal">{col.label}</TableHead>
                            ))}
                            <TableHead className="w-[130px] px-2 text-right">Acciones</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {elementosFiltrados.map((elemento) => {
                            const st = elementoEstados[elemento.id] || { resultado: 'pendiente' as ResultadoElemento, observaciones: '' };
                            return (
                              <TableRow
                                key={elemento.id}
                                className={cn(
                                  st.resultado === 'ok' && 'bg-green-50/60 dark:bg-green-950/20',
                                  st.resultado === 'anomalia' && 'bg-orange-50/60 dark:bg-orange-950/20',
                                )}
                              >
                                <TableCell className="px-2 align-top">
                                  <div className="flex flex-col items-center gap-1">
                                    {getEstadoIcon(st.resultado)}
                                    <AnomaliasIndicator
                                      anomalias={anomaliasElementos.filter((a) => a.elemento_id === elemento.id)}
                                      onResolver={(anomaliaId) => handleResolverAnomalia(elemento.id, anomaliaId)}
                                      size="sm"
                                    />
                                  </div>
                                </TableCell>
                                {columnasFinales.map((col) => (
                                  <TableCell key={col.id} className="px-2 align-top break-words whitespace-normal">
                                    {col.id === 'observaciones' ? (
                                      <ObservacionesCell
                                        value={st.observaciones || ''}
                                        onSave={(v) => setElementoEstado(elemento.id, { observaciones: v })}
                                        disabled={isReadOnly}
                                      />
                                    ) : (
                                      <div className="break-words">{col.render(elemento)}</div>
                                    )}
                                  </TableCell>
                                ))}
                                <TableCell className="px-2 align-top">
                                  <div className="flex flex-col items-end gap-1">
                                    <ElementoFotosButton elementoId={elemento.id} elementoNombre={elemento.nombre} />
                                    <Button
                                      size="sm"
                                      variant={st.resultado === 'anomalia' ? 'destructive' : 'outline'}
                                      onClick={() => { setAnomaliaDialog({ elemento }); setDescripcionAnomalia(''); }}
                                      disabled={isReadOnly}
                                      className="text-xs h-7 px-2 w-full"
                                    >
                                      Anomalía
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant={st.resultado === 'ok' ? 'default' : 'outline'}
                                      onClick={() => handleMarcarOk(elemento)}
                                      disabled={isReadOnly}
                                      className="text-xs h-7 px-2 w-full"
                                    >
                                      {st.resultado === 'ok' || st.resultado === 'anomalia' ? 'Quitar' : 'OK'}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => setEditElementoTarget(elemento)}
                                      disabled={isReadOnly}
                                      className="text-xs h-7 px-2 w-full"
                                      title="Editar datos del elemento"
                                    >
                                      <Edit className="h-3.5 w-3.5 mr-1" /> Editar
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => setEliminarElementoTarget(elemento)}
                                      disabled={isReadOnly}
                                      className="text-xs h-7 px-2 w-full text-destructive hover:text-destructive hover:bg-destructive/10"
                                      title="Eliminar elemento del sistema"
                                    >
                                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Eliminar
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                      {elementosFiltrados.length === 0 && (
                        <div className="text-center py-8 text-muted-foreground text-sm">
                          No hay elementos con los filtros aplicados.
                        </div>
                      )}
                    </div>
                  </>
                )}
              </CollapsibleContent>
            </Collapsible>




            {/* Observaciones estructuradas */}
            <div className="space-y-3 pt-2 border-t">
              <div className="flex items-center justify-between">
                <Label className="text-base">Observaciones / Hallazgos</Label>
                {!isReadOnly && (
                  <Button type="button" size="sm" variant="outline" onClick={addObservacion} className="gap-1 h-8">
                    <Plus className="h-3.5 w-3.5" /> Añadir
                  </Button>
                )}
              </div>
              {observaciones.length === 0 && (
                <p className="text-xs text-muted-foreground italic">Sin observaciones registradas.</p>
              )}
              <div className="space-y-2">
                {observaciones.map((o, idx) => (
                  <div key={o.id} className="rounded-lg border p-3 space-y-2 bg-muted/20">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline" className="font-mono shrink-0">#{idx + 1}</Badge>
                      <Select
                        value={o.prioridad}
                        onValueChange={(v) => updateObservacion(o.id, { prioridad: v as Prioridad })}
                        disabled={isReadOnly}
                      >
                        <SelectTrigger className={`h-8 w-[130px] ${PRIORIDAD_STYLES[o.prioridad]}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="alta">Prioridad alta</SelectItem>
                          <SelectItem value="media">Prioridad media</SelectItem>
                          <SelectItem value="baja">Prioridad baja</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select
                        value={o.tipo}
                        onValueChange={(v) => updateObservacion(o.id, { tipo: v as TipoObs })}
                        disabled={isReadOnly}
                      >
                        <SelectTrigger className="h-8 w-[160px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="normativa">Por normativa</SelectItem>
                          <SelectItem value="mejora">Observación / Mejora</SelectItem>
                        </SelectContent>
                      </Select>
                      {!isReadOnly && (
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          onClick={() => removeObservacion(o.id)}
                          className="h-8 w-8 ml-auto text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    {/* Selector de código de anomalía (autojustificación normativa) */}
                    {!isReadOnly && (
                      <AnomaliaSelector
                        tipoSistema={sistemaTipo}
                        onSelect={(cod) => {
                          updateObservacion(o.id, {
                            codigoAnomalia: cod.codigo,
                            referenciaNormativa: cod.referencia_normativa || '',
                            justificacionNormativa: cod.justificacion_normativa || '',
                            tipo: 'normativa',
                            texto: o.texto?.trim() ? o.texto : cod.descripcion,
                          });
                        }}
                      />
                    )}
                    {o.codigoAnomalia && (
                      <div className="rounded-md border border-primary/30 bg-primary/5 p-2 text-xs space-y-1">
                        <div className="flex items-center justify-between gap-2">
                          <Badge variant="outline" className="font-mono">{o.codigoAnomalia}</Badge>
                          {!isReadOnly && (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="h-6 px-2 text-xs"
                              onClick={() => updateObservacion(o.id, {
                                codigoAnomalia: undefined,
                                referenciaNormativa: undefined,
                                justificacionNormativa: undefined,
                              })}
                            >
                              Quitar código
                            </Button>
                          )}
                        </div>
                        {o.referenciaNormativa && (
                          <p className="font-medium">{o.referenciaNormativa}</p>
                        )}
                        {o.justificacionNormativa && (
                          <p className="italic text-muted-foreground">"{o.justificacionNormativa}"</p>
                        )}
                      </div>
                    )}
                    <Textarea
                      rows={2}
                      placeholder="Describe la observación o hallazgo..."
                      value={o.texto}
                      onChange={(e) => updateObservacion(o.id, { texto: e.target.value })}
                      disabled={isReadOnly}
                    />
                    {/* Fotos de la observación */}
                    <div className="space-y-2">
                      <div className="flex flex-wrap gap-2">
                        {(o.fotos || []).map(path => {
                          const url = fotoUrls[path];
                          if (!url) ensureFotoUrl(path);
                          return (
                            <div key={path} className="relative group w-20 h-20 rounded-md overflow-hidden border bg-muted">
                              {url ? (
                                <img src={url} alt="Foto observación" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                </div>
                              )}
                              {!isReadOnly && (
                                <button
                                  type="button"
                                  onClick={() => removeFotoObservacion(o.id, path)}
                                  className="absolute top-0.5 right-0.5 bg-destructive text-destructive-foreground rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {!isReadOnly && (
                        <label className="inline-flex items-center gap-2 text-xs cursor-pointer text-muted-foreground hover:text-foreground">
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            capture="environment"
                            className="hidden"
                            onChange={(e) => { uploadFotosObservacion(o.id, e.target.files); e.currentTarget.value = ''; }}
                          />
                          <Camera className="h-4 w-4" /> Añadir fotos
                        </label>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="sticky bottom-0 -mx-4 md:-mx-6 px-4 md:px-6 py-3 border-t bg-background/95 backdrop-blur flex flex-row justify-between gap-2">
          <Button variant="ghost" onClick={handleClose}>Cerrar</Button>
          <div className="flex gap-2">
            {!isReadOnly && tipoConfirmado && (
              <Button variant="outline" onClick={() => guardarBorrador(false)} disabled={saving} className="gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Guardar borrador
              </Button>
            )}
            {!isReadOnly && tipoConfirmado && (
              <Button onClick={finalizar} disabled={finalizing} className="gap-2">
                {finalizing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Finalizar checklist
              </Button>
            )}
          </div>
        </div>
      </div>

      <Dialog open={!!anomaliaDialog} onOpenChange={(o) => { if (!o) { setAnomaliaDialog(null); setDescripcionAnomalia(''); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Registrar Anomalía — {anomaliaDialog?.elemento.nombre}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Código de Anomalía</label>
              <AnomaliaSelector
                tipoSistema={sistemaTipo}
                onSelect={registrarAnomalia}
                disabled={loadingAnom}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Descripción adicional (opcional)</label>
              <Textarea
                placeholder="Descripción adicional de la anomalía..."
                value={descripcionAnomalia}
                onChange={(e) => setDescripcionAnomalia(e.target.value)}
                rows={3}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmFinalizar} onOpenChange={(o) => !o && setConfirmFinalizar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Quedan elementos sin probar</AlertDialogTitle>
            <AlertDialogDescription>
              Hay <strong>{confirmFinalizar?.pendientes}</strong> elemento(s) sin marcar. Aparecerán como <em>"No probado"</em> en el informe consolidado. ¿Finalizar de todos modos?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={finalizarConfirmado} disabled={finalizing}>
              {finalizing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Finalizar igualmente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      <ElementoEditDialog
        open={addElementoOpen}
        onOpenChange={setAddElementoOpen}
        sistemaId={sistemaId}
        sistemaTipo={sistemaTipo}
        elemento={null}
        onSaved={(id) => { handleElementoCreado(id); setAddElementoOpen(false); }}
      />

      <ElementoEditDialog
        open={!!editElementoTarget}
        onOpenChange={(o) => { if (!o) setEditElementoTarget(null); }}
        sistemaId={sistemaId}
        sistemaTipo={sistemaTipo}
        elemento={editElementoTarget}
        onSaved={(id) => { refrescarElemento(id); setEditElementoTarget(null); }}
      />



      <AlertDialog open={!!eliminarElementoTarget} onOpenChange={(o) => !o && setEliminarElementoTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar elemento del sistema</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a eliminar <strong>{eliminarElementoTarget?.nombre}</strong> del sistema. Esta acción es permanente y también borrará su estado en esta revisión.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={eliminandoElemento}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleEliminarElemento(); }}
              disabled={eliminandoElemento}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {eliminandoElemento ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
